<template>
  <v-form @submit.prevent="submitForm">
    <v-text-field
      ref="emailInput"
      type="email"
      v-model="email"
      label="Email"
      required
      autocomplete="email"
      variant="outlined"
      :color="foregroundColor"
      class="mb-2 scroll-to-top-on-focus"
      @update:focused="onFieldFocusChange($event, emailInput)"
    />
    <v-text-field
      ref="passwordInput"
      v-model="password"
      label="Password"
      :type="showPassword ? 'text' : 'password'"
      required
      autocomplete="current-password"
      :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
      @click:append-inner="showPassword = !showPassword"
      variant="outlined"
      :color="foregroundColor"
      class="mb-1 scroll-to-top-on-focus"
      @update:focused="onFieldFocusChange($event, passwordInput)"
    />
    <v-checkbox
      v-model="agreement"
      required
      hide-details
      class="mb-1 terms-checkbox"
      :style="{ color: foregroundColor }"
    >
      <template v-slot:label>
        <span style="display: inline; white-space: normal">
          I agree to the
          <span
            @click.stop.prevent="showTerms = true"
            :style="{
              color: foregroundColor,
              textDecoration: 'underline',
              cursor: 'pointer',
            }"
          >
            Terms of service
          </span>
        </span>
      </template>
    </v-checkbox>
    <!--
      Marketing consent is a separate, unticked choice that never blocks
      enrolment (UK GDPR/PECR). The terms box above hides its details row so
      the two read as one group; this one keeps it so the gap before the
      button is unchanged.
    -->
    <v-checkbox
      v-model="marketingConsent"
      class="mb-1 marketing-consent-checkbox"
      :style="{ color: foregroundColor }"
    >
      <template v-slot:label>
        <span style="display: inline; white-space: normal">
          {{ marketingConsentLabelText }}
        </span>
      </template>
    </v-checkbox>
    <v-btn
      type="button"
      block
      @click="submitForm"
      class="enrol-btn"
      :style="{
        backgroundColor: foregroundColor,
        color: backgroundColor,
      }"
    >
      Sign in & enrol
    </v-btn>
    <OverlayModal
      v-model="showTerms"
      :formattedTerms="formattedTerms"
      :foregroundColor="foregroundColor"
      :backgroundColor="backgroundColor"
    />
    <div class="forgot-password-wrapper">
      <span
        @click="openPasswordResetDialog"
        class="forgot-password-link"
        :style="{ color: foregroundColor }"
      >
        Forgot password?
      </span>
    </div>
  </v-form>
  <OverlayDialog
    v-model="showDialog"
    :title="dialogTitle"
    :message="dialogMessage"
    :foregroundColor="foregroundColor"
    :backgroundColor="backgroundColor"
    @update:modelValue="onDialogClosed"
  />
  <OverlayDialog
    v-model="showResetPasswordDialog"
    :title="passwordResetDialogTitle"
    :foregroundColor="foregroundColor"
    :backgroundColor="backgroundColor"
    buttonText="Cancel"
    :secondaryStyle="true"
  >
    <v-text-field
      v-model="email"
      label="Email"
      required
      class="mb-4"
      autocomplete="email"
      :style="{ color: foregroundColor }"
    />
    <v-btn
      @click="forgotPasswordClicked"
      :loading="isLoading"
      :disabled="isLoading"
      class="reset-email-btn"
      :style="{
        backgroundColor: foregroundColor,
        color: backgroundColor,
        textTransform: 'none',
        width: '100%',
        fontWeight: '600',
        fontSize: '14px',
        borderRadius: '999px',
        padding: '8px 22px',
        border: 'none',
      }"
      block
      dense
    >
      Send reset email
    </v-btn>
  </OverlayDialog>
</template>

<script>
import OverlayModal from "@/components/Molecules/OverlayModal.vue";
import OverlayDialog from "@/components/Molecules/OverlayDialog.vue";
import terms from "../../termsOfService.js";
import submitSignupForm from "../../submissionHandler.js";
import { marketingConsentLabel } from "../../utils/marketingConsent.js";
import { sendResetEmail } from "../../firebase-authentication.js";
import { track } from "../../analytics.js";
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";

export default {
  emits: ["isLoading", "successfulSubmission"],
  components: {
    OverlayModal,
    OverlayDialog,
  },
  props: {
    businessId: {
      type: String,
      default: "",
    },
    foregroundColor: {
      type: String,
      default: "rgb(0, 0, 0)",
    },
    backgroundColor: {
      type: String,
      default: "rgb(255, 255, 255)",
    },
    // Names the business in the marketing consent label; "" falls back to
    // the generic wording.
    businessName: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const email = ref("");
    const emailInput = ref(null);
    const password = ref("");
    const passwordInput = ref(null);
    const showPassword = ref(false);
    const agreement = ref(false);
    const marketingConsent = ref(false);
    const marketingConsentLabelText = computed(() =>
      marketingConsentLabel(props.businessName)
    );
    const showTerms = ref(false);
    const isLoading = ref(false);

    const showDialog = ref(false);
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const dialogClosedCallback = ref(null);

    const showResetPasswordDialog = ref(false);
    const passwordResetDialogTitle = ref("Password reset");
    const termsOfService = terms.termsOfService;

    const formattedTerms = computed(() => {
      return termsOfService
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    });

    const openPasswordResetDialog = () => {
      track("password_reset_dialog_opened", { business_id: props.businessId });
      showResetPasswordDialog.value = true;
    };

    const forgotPasswordClicked = async () => {
      isLoading.value = true;
      try {
        await sendResetEmail(email.value);
        track("password_reset_requested", {});
        showResetPasswordDialog.value = false;
        showDialog.value = true;
        dialogTitle.value = "Password reset email sent ✅";
        dialogMessage.value = "Please check your email inbox and follow the steps to reset your password";
      } catch (error) {
        track("password_reset_failed", { error_message: error.message, stage: "request" });
        showResetPasswordDialog.value = false;
        showDialog.value = true;
        dialogTitle.value = "Password reset failed";
        dialogMessage.value = error.message;
      } finally {
        isLoading.value = false;
      }
    };

    const submitForm = async () => {
      emit("isLoading", true);
      isLoading.value = true;
      try {
        const deliveryInfo = await submitSignupForm(
          props.businessId,
          email.value,
          password.value,
          agreement.value,
          true,
          marketingConsent.value
        );
        emit("isLoading", false);
        isLoading.value = false;
        emit("successfulSubmission", deliveryInfo);
      } catch (error) {
        await nextTick();
        emit("isLoading", false);
        isLoading.value = false;

        if (error.message == "NEEDS_LOGIN") {
          track("enrollment_wrong_password", { business_id: props.businessId });
          await presentDialog(
            "Sign in error",
            "Oops! That password didn’t match. Please try again or tap ‘Forgot Password?’ to reset it."
          );
          return;
        }

        showDialog.value = true;
        dialogTitle.value = error.code ? "Sign in error" : "Enrollment error";
        dialogMessage.value = error.message;
      }
    };

    const presentDialog = (title, message) => {
      dialogTitle.value = title;
      dialogMessage.value = message;
      showDialog.value = true;

      return new Promise((resolve) => {
        dialogClosedCallback.value = resolve;
      });
    };

    const onDialogClosed = () => {
      if (dialogClosedCallback.value) {
        dialogClosedCallback.value(true);
        dialogClosedCallback.value = null;
      }
    };

    // Force the typed/autofilled text color to the brand foreground on both
    // fields so the password text matches the email text.
    const applyInputStyles = () => {
      [emailInput.value, passwordInput.value].forEach((field) => {
        const inputEl = field?.$el?.querySelector("input");
        if (inputEl) {
          inputEl.style.webkitTextFillColor = props.foregroundColor;
          inputEl.style.color = props.foregroundColor;
          inputEl.style.backgroundColor = "transparent";
        }
      });
    };

    onMounted(() => {
      applyInputStyles();
    });

    watch([email, password], () => {
      applyInputStyles();
    });

    // When a field enters editing mode, scroll the page so the field sits at
    // the top (the `.scroll-to-top-on-focus` scroll-margin keeps a little room
    // above it) rather than wherever the browser leaves it - on phones that is
    // often half-hidden behind the keyboard. The scroll waits for the keyboard
    // to finish opening so the measurement reflects the shrunken viewport, and
    // is cancelled if focus moves on before then.
    const FOCUS_SCROLL_DELAY_MS = 300;
    let focusScrollTimer = null;

    const onFieldFocusChange = (focused, field) => {
      clearTimeout(focusScrollTimer);
      if (!focused) return;
      focusScrollTimer = setTimeout(() => {
        field?.$el?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      }, FOCUS_SCROLL_DELAY_MS);
    };

    onBeforeUnmount(() => clearTimeout(focusScrollTimer));

    return {
      email,
      emailInput,
      password,
      passwordInput,
      showPassword,
      agreement,
      marketingConsent,
      marketingConsentLabelText,
      showTerms,
      showDialog,
      dialogTitle,
      dialogMessage,
      forgotPasswordClicked,
      openPasswordResetDialog,
      showResetPasswordDialog,
      passwordResetDialogTitle,
      isLoading,
      submitForm,
      onDialogClosed,
      onFieldFocusChange,
      formattedTerms,
    };
  },
};
</script>

<style scoped>
.enrol-btn {
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 999px !important;
  text-transform: none !important;
  height: 48px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  letter-spacing: 0.2px !important;
}

.enrol-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
}

.reset-email-btn {
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.reset-email-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
}

.forgot-password-wrapper {
  text-align: center;
  margin-top: 12px;
  margin-bottom: 4px;
}

.forgot-password-link {
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.forgot-password-link:hover {
  opacity: 1;
}

/* Breathing room kept above a field when focusing scrolls it to the top. */
.scroll-to-top-on-focus {
  scroll-margin-top: 16px;
}

</style>

<style>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  transition: background-color 9999s ease-in-out 0s !important;
  -webkit-transition: background-color 9999s ease-in-out 0s !important;
  background-color: transparent !important;
  -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
  box-shadow: 0 0 0px 1000px transparent inset !important;
  -webkit-text-fill-color: var(--foregroundColor) !important;
}

input:-webkit-autofill {
  -webkit-text-fill-color: var(--foregroundColor) !important;
  transition: background-color 9999s ease-in-out 0s !important;
  background-color: transparent !important;
  box-shadow: 0 0 0px 1000px transparent inset !important;
}
</style>
